import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminRates } from '../../../hooks/useAdminRates';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import toast from 'react-hot-toast';
import { useModal } from '../../../hooks/useModal';
import { Download, Upload, Copy, Save, Pencil, X, Loader2 } from 'lucide-react';
import B2bRatesPage from '../../b2b/rates';

export default function AdminRatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabType = (searchParams.get('type') || 'b2c').toLowerCase();

  const { useAllPlansQuery, usePlanRatesQuery, useSaveAsNewPlanMutation, useUpdatePlanRatesMutation, useUploadRatesMutation, useUploadAndReplaceRatesMutation, downloadSampleRates } = useAdminRates();

  const { data: plansData, isLoading: isLoadingPlans } = useAllPlansQuery();
  const rawPlans = plansData?.data || [];
  const plans = useMemo(() => {
    return rawPlans.filter((p: any) => {
      const type = (p.type || '').toLowerCase();
      const name = (p.name || '').toLowerCase();
      return type === 'b2c' || (!type && !name.startsWith('b2b_'));
    });
  }, [rawPlans]);

  const [selectedPlanId, setSelectedPlanId] = useState<string>('');

  const { data: ratesData, isLoading: isLoadingRates } = usePlanRatesQuery(selectedPlanId, { enabled: !!selectedPlanId });
  const rates = ratesData?.data || [];

  const { mutate: saveAsNewPlan, isPending: isSaving } = useSaveAsNewPlanMutation();
  const { mutate: updatePlanRates, isPending: isUpdating } = useUpdatePlanRatesMutation();
  const { mutate: uploadRates, isPending: isUploading } = useUploadRatesMutation();
  const { mutate: uploadAndReplaceRates, isPending: isUploadingAndReplacing } = useUploadAndReplaceRatesMutation();

  const { open, close, Modal } = useModal();
  const [isEditing, setIsEditing] = useState(false);
  const [isCloneMode, setIsCloneMode] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadMode, setUploadMode] = useState<'create' | 'replace'>('create');

  const parseDecimal = (value: string | number) => {
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    return Math.round((parsed || 0) * 100) / 100;
  };

  const formatRate = (val: any) => {
    if (val === 0 || val === 0.0 || val === '0' || val === '0.00') return '0';
    if (!val && val !== 0) return '-';
    return Number(val).toFixed(1);
  };

  // Group rates by courierId-serviceId
  const groupedRates = useMemo(() => {
    const groups: Record<string, any> = {};
    rates.forEach((rate: any) => {
      const key = `${rate.courierId}-${rate.serviceId}`;
      if (!groups[key]) {
        groups[key] = {
          courierId: rate.courierId,
          serviceId: rate.serviceId,
          courierName: rate.courierName,
          serviceName: rate.serviceName,
          mode: rate.mode,
          baseWeight: rate.baseWeightSlab,
          addlWeight: rate.addlWeightSlab,
          codCharges: rate.codCharges,
          codPercent: rate.codPercent,
          fuelSurcharge: rate.fuelSurcharge,
          fuelSurchargeAddl: rate.fuelSurchargeAddl,
          otherCharges: rate.otherCharges,
          rtoCharges: rate.rtoCharges,
          rtoChargesAddl: rate.rtoChargesAddl,
          zones: {}
        };
      }
      groups[key].zones[rate.zone] = { ...rate };
    });
    return Object.values(groups).sort((g1: any, g2: any) => {
      if (g1.mode !== g2.mode) {
        return g1.mode === "Air" ? -1 : 1;
      }
      const w1 = g1.baseWeight || 0.5;
      const w2 = g2.baseWeight || 0.5;
      if (w1 !== w2) {
        return w1 - w2;
      }
      if (g1.courierId !== g2.courierId) {
        return g1.courierId - g2.courierId;
      }
      return g1.serviceId - g2.serviceId;
    });
  }, [ratesData?.data]);

  const [editedGroups, setEditedGroups] = useState<any[]>([]);

  useEffect(() => {
    setEditedGroups(JSON.parse(JSON.stringify(groupedRates)));
  }, [groupedRates]);

  const handleEditChange = (groupIndex: number, zone: string, field: string, value: string) => {
    if (value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    const newGroups = [...editedGroups];
    if (!newGroups[groupIndex].zones[zone]) {
      newGroups[groupIndex].zones[zone] = {
        courierId: newGroups[groupIndex].courierId,
        serviceId: newGroups[groupIndex].serviceId,
        zone: zone,
        baseWeightSlab: newGroups[groupIndex].baseWeight,
        addlWeightSlab: newGroups[groupIndex].addlWeight,
      };
    }
    newGroups[groupIndex].zones[zone][field] = value === '' ? 0 : parseDecimal(value);
    setEditedGroups(newGroups);
  };

  const handleGroupFieldChange = (groupIndex: number, field: string, value: string) => {
    if (value && !/^\d*\.?\d{0,2}$/.test(value)) return;
    const newGroups = [...editedGroups];
    const numValue = value === '' ? 0 : parseDecimal(value);
    newGroups[groupIndex][field] = numValue;

    Object.keys(newGroups[groupIndex].zones).forEach(z => {
      newGroups[groupIndex].zones[z][field] = numValue;
    });

    setEditedGroups(newGroups);
  };

  const handleSaveAsNew = () => {
    open({
      Component: () => {
        const [planName, setPlanName] = useState('');
        return (
          <div className="bg-white p-6 rounded-xl w-96">
            <h2 className="text-xl font-bold mb-4">Save As New Plan</h2>
            <Input
              label="New Plan Name"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g. Premium Plan v2"
            />
            <div className="flex gap-4 mt-6">
              <Button variant="outline" className="flex-1" onClick={close}>Cancel</Button>
              <Button
                className="flex-1"
                disabled={!planName.trim() || isSaving}
                onClick={() => {
                  if (plans.some((p: any) => p.name.toLowerCase() === planName.trim().toLowerCase())) {
                    toast.error('Plan name must be unique');
                    return;
                  }
                   const flatRates: any[] = [];
                   editedGroups.forEach(g => {
                     Object.values(g.zones).forEach((z: any) => {
                       flatRates.push({
                         ...z,
                         codCharges: g.codCharges,
                         codPercent: g.codPercent,
                         fuelSurcharge: g.fuelSurcharge,
                         fuelSurchargeAddl: g.fuelSurchargeAddl,
                         otherCharges: g.otherCharges,
                         // rtoCharges and addRtoCharges are already inside z
                       });
                     });
                   });
                   saveAsNewPlan({ name: planName, rates: flatRates }, {
                     onSuccess: () => {
                       toast.success('Plan saved as new');
                       setIsEditing(false);
                       close();
                     },
                     onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to save plan')
                   });
                 }}
               >
                 {isSaving ? (
                   <>
                     <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                     Saving...
                   </>
                 ) : (
                   'Save Plan'
                 )}
               </Button>
             </div>
           </div>
         );
       },
       centered: true,
       closable: true,
       className: 'bg-transparent shadow-none',
     });
   };
 
   const handleSaveChanges = () => {
     const flatRates: any[] = [];
     editedGroups.forEach(g => {
       Object.values(g.zones).forEach((z: any) => {
         flatRates.push({
           ...z,
           codCharges: g.codCharges,
           codPercent: g.codPercent,
           fuelSurcharge: g.fuelSurcharge,
           fuelSurchargeAddl: g.fuelSurchargeAddl,
           otherCharges: g.otherCharges,
           // rtoCharges and rtoChargesAddl are already inside z
         });
       });
     });
 
     updatePlanRates({ planId: selectedPlanId, payload: { rates: flatRates } }, {
       onSuccess: () => {
         toast.success('Plan rates updated successfully');
         setIsEditing(false);
       },
       onError: (err: any) => toast.error(err.response?.data?.message || 'Failed to update plan rates')
     });
   };

  const fileInputRef = useRef<HTMLInputElement>(null);

   const handleUploadClick = () => {
     setUploadMode('create');
     fileInputRef.current?.click();
   };
 
   const handleUploadReplaceClick = () => {
     setUploadMode('replace');
     fileInputRef.current?.click();
   };
 
   const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
     const file = e.target.files?.[0];
     if (file) {
       if (uploadMode === 'replace') {
         if (!selectedPlanId) return;
         open({
           Component: () => (
             <div className="bg-white p-6 rounded-xl w-96 font-sans">
               <h2 className="text-xl font-bold mb-4">Replace Plan Rates</h2>
               <p className="text-text2 mb-6">Are you sure you want to replace all rates in the current plan with the ones from the uploaded sheet?</p>
               <div className="flex gap-4">
                 <Button variant="outline" className="flex-1" onClick={() => {
                   if (fileInputRef.current) fileInputRef.current.value = '';
                   close();
                 }}>Cancel</Button>
                 <Button
                   className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                   disabled={isUploadingAndReplacing}
                   onClick={() => {
                     uploadAndReplaceRates({ planId: selectedPlanId, file }, {
                       onSuccess: () => {
                         toast.success('Rates replaced successfully');
                         if (fileInputRef.current) fileInputRef.current.value = '';
                         close();
                       },
                       onError: (err: any) => {
                         toast.error(err.response?.data?.message || 'Failed to replace rates');
                         if (fileInputRef.current) fileInputRef.current.value = '';
                         close();
                       }
                     });
                   }}
                 >
                   {isUploadingAndReplacing ? (
                     <>
                       <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                       Replacing...
                     </>
                   ) : (
                     'Confirm Replace'
                   )}
                 </Button>
               </div>
             </div>
           ),
           centered: true,
           closable: true,
           className: 'bg-transparent shadow-none',
         });
       } else {
         open({
           Component: () => {
             const [planName, setPlanName] = useState('');
             return (
               <div className="bg-white p-6 rounded-xl w-96">
                 <h2 className="text-xl font-bold mb-4">Upload as New Plan</h2>
                 <Input
                   label="New Plan Name"
                   value={planName}
                   onChange={(evt) => setPlanName(evt.target.value)}
                   placeholder="e.g. Bulk Upload 2026"
                 />
                 <div className="flex gap-4 mt-6">
                   <Button variant="outline" className="flex-1" onClick={() => {
                     if (fileInputRef.current) fileInputRef.current.value = '';
                     close();
                   }}>Cancel</Button>
                   <Button
                     className="flex-1"
                     disabled={!planName.trim() || isUploading}
                     onClick={() => {
                       if (plans.some((p: any) => p.name.toLowerCase() === planName.trim().toLowerCase())) {
                         toast.error('Plan name must be unique');
                         return;
                       }
                       uploadRates({ planName, file }, {
                         onSuccess: () => {
                           toast.success('Rates uploaded and new plan created');
                           if (fileInputRef.current) fileInputRef.current.value = '';
                           close();
                         },
                         onError: (err: any) => {
                           toast.error(err.response?.data?.message || 'Failed to upload rates');
                           if (fileInputRef.current) fileInputRef.current.value = '';
                           close();
                         }
                       });
                     }}
                   >
                     {isUploading ? (
                       <>
                         <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                         Uploading...
                       </>
                     ) : (
                       'Upload & Create'
                     )}
                   </Button>
                 </div>
               </div>
             );
           },
           centered: true,
           closable: true,
           className: 'bg-transparent shadow-none',
         });
       }
     }
   };

  const ZONES = ['zone-1', 'zone-2', 'zone-3', 'zone-4', 'zone-5'];

  return (
    <div className="p-6 space-y-6">
      {/* Category Mode Switcher */}
      <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
        <button
          type="button"
          onClick={() => setSearchParams({ type: 'b2c' })}
          className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
            activeTabType !== 'b2b'
              ? 'bg-teal text-white shadow-md'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          <span>🛍️ B2C Rate Plans</span>
        </button>

        <button
          type="button"
          onClick={() => setSearchParams({ type: 'b2b' })}
          className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
            activeTabType === 'b2b'
              ? 'bg-teal text-white shadow-md'
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          <span>🚛 B2B Rate Plans</span>
        </button>
      </div>

      {activeTabType === 'b2b' ? (
        <B2bRatesPage hidePadding />
      ) : (
        <>
          <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-border">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-text">Plan Rates Management</h1>
          <div className="w-64">
            <Select
              label=""
              options={[
                { label: 'Select a Plan...', value: '' },
                ...(plans || []).map((p: any) => ({ label: p.name, value: String(p.id) }))
              ]}
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {selectedPlanId && !isEditing && (
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => {
                setIsEditing(true);
                setIsCloneMode(true);
              }}
            >
              <Copy size={16} /> Clone Plan
            </Button>
          )}

          {selectedPlanId && (
            <Button
              variant={isEditing && !isCloneMode ? "default" : "outline"}
              className="flex items-center gap-2"
              onClick={() => {
                setIsEditing(!isEditing);
                setIsCloneMode(false);
              }}
            >
              {isEditing && !isCloneMode ? <><X size={16} /> Cancel Edit</> : <><Pencil size={16} /> Edit Rates</>}
            </Button>
          )}

          {isEditing && selectedPlanId && (
            <div className="flex gap-2">
              {!isCloneMode && (
                <Button
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                  onClick={handleSaveChanges}
                  disabled={isSaving || isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} /> Save Changes
                    </>
                  )}
                </Button>
              )}
              <Button
                variant={isCloneMode ? "default" : "outline"}
                className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700 hover:text-white"
                onClick={handleSaveAsNew}
                disabled={isSaving || isUpdating}
              >
                <Save size={16} /> Save As New Plan
              </Button>
            </div>
          )}

          <Button
            variant="outline"
            className="flex items-center gap-2"
            disabled={isDownloading}
            onClick={async () => {
              try {
                setIsDownloading(true);
                const blob = await downloadSampleRates(selectedPlanId || undefined);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = selectedPlanId ? `rates_plan_${selectedPlanId}.xlsx` : 'rates_template.xlsx';
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
              } catch (err) {
                toast.error("Failed to download sample rates");
              } finally {
                setIsDownloading(false);
              }
            }}
          >
            {isDownloading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download size={16} /> Download {selectedPlanId ? 'Plan Rates' : 'Template'}
              </>
            )}
          </Button>

          <input
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileUpload}
          />
          <Button
            variant="outline"
            className="flex items-center gap-2"
            disabled={isUploading}
            onClick={handleUploadClick}
          >
            <Upload size={16} /> Upload New Plan
          </Button>

          {selectedPlanId && (
            <Button
              variant="outline"
              className="flex items-center gap-2"
              disabled={isUploading || isUploadingAndReplacing}
              onClick={handleUploadReplaceClick}
            >
              <Upload size={16} /> Upload & Replace Rates
            </Button>
          )}
        </div>
      </div>

      {!selectedPlanId && (
        <div className="bg-white p-12 rounded-xl text-center text-text3 shadow-sm border border-border">
          Please select a plan from the dropdown to view or edit rates.
        </div>
      )}

      {selectedPlanId && isLoadingRates && (
        <div className="text-center py-12">Loading rates...</div>
      )}

      {selectedPlanId && !isLoadingRates && editedGroups.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-[#008080] text-white">
                <tr>
                  <th rowSpan={2} className="px-4 py-3 font-semibold whitespace-nowrap text-center align-middle border-b border-r border-white/20">COURIER</th>
                  <th rowSpan={2} className="px-4 py-3 font-semibold whitespace-nowrap text-center align-middle border-b border-r border-white/20">WEIGHT</th>
                  <th colSpan={2} className="px-4 py-2 font-semibold whitespace-nowrap text-center border-b border-r border-white/20">ZONE 1<br /><span className="text-xs font-normal">Within-city</span></th>
                  <th colSpan={2} className="px-4 py-2 font-semibold whitespace-nowrap text-center border-b border-r border-white/20">ZONE 2<br /><span className="text-xs font-normal">Within-State</span></th>
                  <th colSpan={2} className="px-4 py-2 font-semibold whitespace-nowrap text-center border-b border-r border-white/20">ZONE 3<br /><span className="text-xs font-normal">Metro to Metro</span></th>
                  <th colSpan={2} className="px-4 py-2 font-semibold whitespace-nowrap text-center border-b border-r border-white/20">ZONE 4<br /><span className="text-xs font-normal">Rest of India</span></th>
                  <th colSpan={2} className="px-4 py-2 font-semibold whitespace-nowrap text-center border-b border-r border-white/20">ZONE 5<br /><span className="text-xs font-normal">NE and J&K</span></th>
                  <th rowSpan={2} className="px-4 py-3 font-semibold whitespace-nowrap text-center align-middle border-b border-r border-white/20">COD (INR)</th>
                  <th rowSpan={2} className="px-4 py-3 font-semibold whitespace-nowrap text-center align-middle border-b border-r border-white/20">COD (%)</th>
                  <th rowSpan={2} className="px-4 py-3 font-semibold whitespace-nowrap text-center align-middle border-b border-r border-white/20">FUEL (BASE %)</th>
                  <th rowSpan={2} className="px-4 py-3 font-semibold whitespace-nowrap text-center align-middle border-b border-r border-white/20">FUEL (ADDL %)</th>
                  <th rowSpan={2} className="px-4 py-3 font-semibold whitespace-nowrap text-center align-middle border-b">OTHER (INR)</th>
                </tr>
                <tr className="bg-[#007070]">
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">FWD</th>
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">RTO</th>
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">FWD</th>
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">RTO</th>
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">FWD</th>
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">RTO</th>
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">FWD</th>
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">RTO</th>
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">FWD</th>
                  <th className="px-2 py-1 text-xs font-semibold text-center border-r border-white/20">RTO</th>
                </tr>
              </thead>
              <tbody>
                {editedGroups.map((group, idx) => (
                  <React.Fragment key={`${group.courierId}-${group.serviceId}`}>
                    {/* Base Rate Row */}
                    <tr className="border-b border-border bg-gray-50/30">
                      <td className="px-4 py-3 font-medium text-white bg-[#008080] border-r border-white/20 align-middle text-center row-span-2" rowSpan={2}>
                        {group.courierName} {group.serviceName}
                        <div className="text-xs text-white/80 mt-1 font-normal">
                          By {group.mode || 'Air'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">{group.baseWeight} KG</td>
                      {ZONES.map(z => (
                        <React.Fragment key={z}>
                          <td className="px-2 py-3 text-center border-r border-gray-200">
                            {isEditing ? (
                              <input
                                type="number"
                                className="w-16 border rounded px-1 py-1 text-center"
                                value={group.zones[z]?.baseRate || 0}
                                onChange={(e) => handleEditChange(idx, z, 'baseRate', e.target.value)}
                              />
                            ) : (
                              <span>{formatRate(group.zones[z]?.baseRate)}</span>
                            )}
                          </td>
                          <td className="px-2 py-3 text-center border-r border-gray-300 bg-red-50/20">
                            {isEditing ? (
                              <input
                                type="number"
                                className="w-16 border rounded px-1 py-1 text-center text-red-600"
                                value={group.zones[z]?.rtoCharges || 0}
                                onChange={(e) => handleEditChange(idx, z, 'rtoCharges', e.target.value)}
                              />
                            ) : (
                              <span className="font-semibold text-red-600">{formatRate(group.zones[z]?.rtoCharges)}</span>
                            )}
                          </td>
                        </React.Fragment>
                      ))}
                      <td className="px-4 py-3 text-center align-middle" rowSpan={2}>
                        {isEditing ? (
                          <input
                            type="number"
                            className="w-24 border rounded px-2 py-1 text-center"
                            value={group.codCharges || 0}
                            onChange={(e) => handleGroupFieldChange(idx, 'codCharges', e.target.value)}
                          />
                        ) : (
                          <span>{formatRate(group.codCharges)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center align-middle" rowSpan={2}>
                        {isEditing ? (
                          <input
                            type="number"
                            className="w-24 border rounded px-2 py-1 text-center"
                            value={group.codPercent || 0}
                            onChange={(e) => handleGroupFieldChange(idx, 'codPercent', e.target.value)}
                          />
                        ) : (
                          <span>{formatRate(group.codPercent)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center align-middle" rowSpan={2}>
                        {isEditing ? (
                          <input
                            type="number"
                            className="w-24 border rounded px-2 py-1 text-center"
                            value={group.fuelSurcharge || 0}
                            onChange={(e) => handleGroupFieldChange(idx, 'fuelSurcharge', e.target.value)}
                          />
                        ) : (
                          <span>{formatRate(group.fuelSurcharge)}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center align-middle" rowSpan={2}>
                        {isEditing ? (
                          <input
                            type="number"
                            className="w-24 border rounded px-2 py-1 text-center"
                            value={group.fuelSurchargeAddl || 0}
                            onChange={(e) => handleGroupFieldChange(idx, 'fuelSurchargeAddl', e.target.value)}
                          />
                        ) : (
                          <span>{formatRate(group.fuelSurchargeAddl)}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center align-middle" rowSpan={2}>
                        {isEditing ? (
                          <input
                            type="number"
                            className="w-24 border rounded px-2 py-1 text-center"
                            value={group.otherCharges || 0}
                            onChange={(e) => handleGroupFieldChange(idx, 'otherCharges', e.target.value)}
                          />
                        ) : (
                          <span>{formatRate(group.otherCharges)}</span>
                        )}
                      </td>
                    </tr>
                    {/* Additional Rate Row */}
                    <tr className="border-b-2 border-gray-200">
                      <td className="px-4 py-3 text-text3 whitespace-nowrap text-center">Add {group.addlWeight}</td>
                      {ZONES.map(z => (
                        <React.Fragment key={z}>
                          <td className="px-2 py-3 text-center border-r border-gray-200">
                            {isEditing ? (
                              <input
                                type="number"
                                className="w-16 border rounded px-1 py-1 text-center bg-gray-50"
                                value={group.zones[z]?.baseRateAddl || 0}
                                onChange={(e) => handleEditChange(idx, z, 'baseRateAddl', e.target.value)}
                              />
                            ) : (
                              <span className="text-text3">{formatRate(group.zones[z]?.baseRateAddl)}</span>
                            )}
                          </td>
                          <td className="px-2 py-3 text-center border-r border-gray-300 bg-red-50/20">
                            {isEditing ? (
                              <input
                                type="number"
                                className="w-16 border rounded px-1 py-1 text-center text-red-600"
                                value={group.zones[z]?.rtoChargesAddl || 0}
                                onChange={(e) => handleEditChange(idx, z, 'rtoChargesAddl', e.target.value)}
                              />
                            ) : (
                              <span className="text-text3 text-red-600">{formatRate(group.zones[z]?.rtoChargesAddl)}</span>
                            )}
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedPlanId && !isLoadingRates && editedGroups.length === 0 && (
        <div className="bg-white p-12 rounded-xl text-center text-text3 shadow-sm border border-border">
          No rates found for this plan. You can upload a new rates template.
        </div>
      )}
      <Modal />
        </>
      )}
    </div>
  );
}
